/**
 * FeedbackModal — form for submitting feedback to the relay.
 * Category, severity, free text. Auto-captures context.
 */

import { useState } from 'react';
import { Modal } from '@/components/atoms/Modal';
import { cn } from '@/lib/utils';
import { submitFeedback, type FeedbackPayload } from '@/lib/feedbackClient';
import type { FeedbackContext } from '@/hooks/useFeedbackContext';

const CATEGORIES = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'pricing-wrong', label: 'Pricing Wrong' },
  { value: 'ux-friction', label: 'UX Friction' },
] as const;

const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'annoying', label: 'Annoying' },
  { value: 'suggestion', label: 'Suggestion' },
] as const;

type Category = FeedbackPayload['category'];
type Severity = FeedbackPayload['severity'];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: FeedbackContext;
}

export function FeedbackModal({ isOpen, onClose, context }: FeedbackModalProps) {
  const [category, setCategory] = useState<Category>('bug');
  const [severity, setSeverity] = useState<Severity>('annoying');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = text.trim().length >= 10 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        category,
        severity,
        text: text.trim(),
        route: context.route,
        sport: context.sport,
        marketType: context.marketType,
        buildHash: context.buildHash,
        viewport: context.viewport,
        connectionStatus: context.connectionStatus,
        timestamp: new Date().toISOString(),
      });
      setSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setText('');
    setCategory('bug');
    setSeverity('annoying');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Send Feedback"
      description="Help us improve the trading experience"
      size="sm"
      footer={
        submitted ? null : (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">
              {text.trim().length < 10
                ? `${10 - text.trim().length} more chars needed`
                : 'Ready to submit'}
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                  canSubmit
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                {submitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )
      }
    >
      {submitted ? (
        <div className="py-6 text-center">
          <p className="text-lg font-medium">Thanks for your feedback!</p>
          <p className="text-muted-foreground mt-1 text-sm">We'll review it shortly.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Category */}
          <div>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase tracking-wider">
              Category
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    category === c.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Severity */}
          <div>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase tracking-wider">
              Severity
            </label>
            <div className="flex gap-1.5">
              {SEVERITIES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSeverity(s.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    severity === s.value
                      ? s.value === 'critical'
                        ? 'bg-red-500 text-white'
                        : s.value === 'annoying'
                          ? 'bg-yellow-500 text-black'
                          : 'bg-blue-500 text-white'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text */}
          <div>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase tracking-wider">
              Description
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe what happened or what you'd like to see..."
              rows={4}
              className={cn(
                'bg-muted border-border w-full rounded-md border px-3 py-2 text-sm',
                'placeholder:text-muted-foreground/60 focus:border-primary focus:ring-primary focus:outline-none focus:ring-1'
              )}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
